import { Announcements } from '../../components/Announcements';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { PageLoad } from '../lib/bookingPage';
import { applyAccent, loadBookingPage } from '../lib/bookingPage';
import { BookingFlow } from '../components/BookingFlow';
import { ServiceRequestForm } from '../components/ServiceRequestForm';
import { BusinessHeader, ContactList, PageFooter, Policies, hasContact } from '../components/BusinessHeader';
import { hasPaymentInfo, PaymentMethods } from '../components/Payment';
import { NotFoundPage } from './NotFoundPage';
import { industryLabel, industryPicture, isOrderFirstIndustry } from '@shared/industries';
import { I18nProvider, useI18n } from '../lib/i18n';
import type { PublicIndustryPage } from '../types';

/**
 * The other trades this business works in.
 *
 * One business may publish a page per trade, and a customer who arrives
 * at one of them — from a QR code on a card, say — has no other way to
 * discover the rest. This is that way. It is deliberately quiet: a single
 * row of links, and nothing at all for the great majority of businesses
 * that publish one page.
 *
 * The business's own page is always offered when you are not on it,
 * because that is where every service is listed.
 */
function BusinessSharePanel({ name, page }: { name: string; page: import('../types').PublicPage }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window === 'undefined' ? '' : window.location.href;
  const share = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title: name, text: `Book with ${name}`, url }); return; } catch { /* visitor cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* Copy is still available through the browser menu. */ }
  };
  return (
    <section className="business-share-panel" aria-label="Business links and payments">
      <div className="business-share-actions">
        <button type="button" className="btn btn-primary" onClick={() => void share()}>Share this business</button>
        <button type="button" className="btn btn-secondary" onClick={async () => {
          try { await navigator.clipboard.writeText(url); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } catch { /* no clipboard permission */ }
        }}>{copied ? 'Copied' : 'Copy link'}</button>
      </div>
      {hasContact(page) && (
        <details className="business-connect">
          <summary>Connect with us</summary>
          <ContactList page={page} />
        </details>
      )}
      {hasPaymentInfo(page) && (
        <div className="business-payments">
          <strong>Accepted payments</strong>
          <PaymentMethods page={page} />
        </div>
      )}
    </section>
  );
}

function TradeNav({ slug, businessName, current, pages }: {
  slug: string;
  businessName: string;
  current: string | null;
  pages: PublicIndustryPage[];
}) {
  const { t } = useI18n();
  if (pages.length === 0) return null;
  const base = `/${encodeURIComponent(slug)}`;
  return (
    <nav className="trade-nav" aria-label={t('page.what_we_do')}>
      <ul>
        {current && <li><a href={base}>{t('page.all_of', { name: businessName })}</a></li>}
        {pages.map((trade) => (
          <li key={trade.page_slug}>
            <a href={`${base}/${encodeURIComponent(trade.page_slug)}`}>{industryLabel(trade.industry, trade.label)}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The public page, in the language it is read in.
 *
 * The provider wraps the screen rather than sitting inside it, because
 * the business's own default (`page.locale`, migration 0038) arrives
 * with the data: until it does, a visitor who has chosen a language
 * still gets it, and everybody else gets English rather than a flash of
 * the wrong language.
 */
export function BookingPage() {
  const [businessLocale, setBusinessLocale] = useState<string | null>(null);
  return (
    <I18nProvider businessLocale={businessLocale}>
      <BookingPageContent onBusinessLocale={setBusinessLocale} />
    </I18nProvider>
  );
}

function BookingPageContent({ onBusinessLocale }: { onBusinessLocale: (locale: string | null) => void }) {
  const { t } = useI18n();
  // /:slug is the business's own page; /:slug/:pageSlug is one of its
  // industry pages. Same business either way — only the introduction and
  // the services on offer differ.
  const { slug = '', pageSlug } = useParams();
  const industryPage = pageSlug ?? null;
  const [state, setState] = useState<PageLoad | { kind: 'loading' }>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    loadBookingPage(slug, industryPage).then((r) => {
      if (!cancelled) setState(r);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, industryPage]);

  const data = state.kind === 'ready' ? state.data : null;
  useEffect(() => {
    if (!data) return;
    applyAccent(data.status === 'active' ? data.page.accent : null);
    onBusinessLocale(data.page.locale ?? null);
    const name = data.page.display_name || (data.status === 'active' ? data.organization.name : null);
    if (name) document.title = data.status === 'active' ? t('page.title_book_with', { name }) : name;
  }, [data, onBusinessLocale, t]);

  if (state.kind === 'loading') {
    return (
      <main className="page" aria-busy="true">
        <p className="muted loading-note">{t('page.loading')}</p>
      </main>
    );
  }
  if (state.kind === 'not_found') return <NotFoundPage />;
  if (state.kind === 'disabled') {
    return <NotFoundPage title={t('page.booking_not_open_title')} message={state.message || t('page.booking_not_open_message')} />;
  }
  if (state.kind === 'error') {
    return (
      <main className="page">
        <section className="card">
          <h1>{t('page.something_wrong')}</h1>
          <p role="alert">{state.message}</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>{t('page.try_again')}</button>
        </section>
      </main>
    );
  }

  const page = state.data;
  if (page.status === 'unavailable') {
    const name = page.page.display_name || t('page.this_business');
    return (
      <main className="page">
        <Announcements path={`/public/${encodeURIComponent(slug)}/announcements`} identity={`public:${slug}`}/>
      <BusinessHeader name={name} logoUrl={page.page.logo_url} />
        <section className="card unavailable" aria-labelledby="unavail-h">
          <h2 id="unavail-h">{t('page.unavailable_title')}</h2>
          <p>{page.page.unavailable_message || t('page.unavailable_message', { name })}</p>
          <ContactList page={page.page} />
        </section>
        <PageFooter language={false} />
      </main>
    );
  }

  const { page: p, organization } = page;
  const name = p.display_name || organization.name;
  const tradePicture = industryPicture(p.industry);
  const bookable = page.booking_enabled !== false;
  // A prepared-food or food-truck business (4.1.73) leads with its shop:
  // "Order now" is the page's first action and the booking flow sits
  // beneath it. Only while the shop is published and paid for — the
  // same `page.shop` that decides the quiet link for every other trade.
  const orderFirst = !!page.shop && isOrderFirstIndustry(p.industry);

  return (
    <main className="page">
      <Announcements path={`/public/${encodeURIComponent(slug)}/announcements`} identity={`public:${slug}`}/>
      {/* The business's own cover, or a picture of its trade until it
          uploads one — a page with a banner reads as a place, a bare
          heading reads as a form. */}
      <BusinessHeader name={name} logoUrl={p.logo_url} coverUrl={p.cover_image_url ?? tradePicture.src} coverFit={p.cover_image_url || tradePicture.photo ? 'cover' : 'contain'} headline={p.headline} description={p.description}>
        <ContactList page={p} />
      </BusinessHeader>
      <BusinessSharePanel name={name} page={p} />
      <TradeNav slug={slug} businessName={organization.name} current={industryPage} pages={page.pages ?? []} />
      {/* This business also sells or rents things (0040). One quiet link,
          and nothing at all for a business that does not. */}
      {page.shop && !orderFirst && (
        <nav className="trade-nav" aria-label={t('page.shop_nav')}>
          <ul><li><a href={`/${encodeURIComponent(slug)}/shop`}>{t('page.shop_link')}</a></li></ul>
        </nav>
      )}
      {orderFirst && (
        <section className="card order-first" aria-labelledby="order-first-h">
          <h2 id="order-first-h">{t('page.order_now_title', { name })}</h2>
          <p>{t('page.order_now_lead')}</p>
          <a className="btn btn-primary btn-lg" href={`/${encodeURIComponent(slug)}/shop`}>{t('page.order_now')}</a>
          {bookable && <p className="muted small">{t('page.order_now_book_note')}</p>}
        </section>
      )}
      {/* A service request (0053): work that needs no appointment, taken
          from the page while the business publishes an intake for it.
          Above the booking flow, because a business that took this trade
          leads with it; appointments stay available below. */}
      {page.service_request && !industryPage && (
        <ServiceRequestForm slug={slug} businessName={name} summary={page.service_request} />
      )}
      {bookable ? (
        <BookingFlow key={`${slug}/${industryPage ?? ''}`} slug={slug} data={page} />
      ) : (
        <section className="card" aria-labelledby="soon-h">
          <h2 id="soon-h">{t('page.coming_soon_title')}</h2>
          <p>{t('page.coming_soon_message', { name })}</p>
        </section>
      )}
      <Policies page={p} />
      <PageFooter poweredBy={page.powered_by !== false} language={false} />
    </main>
  );
}

